import {
  QueryClient,
  defaultShouldDehydrateQuery,
  environmentManager,
} from '@tanstack/react-query';

/**
 * Single source of truth for React Query's configuration.
 *
 * Shared by the browser provider and by any Server Component that prefetches,
 * so the cache a server render hands over is configured exactly like the client
 * that adopts it.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Sidebar nav remounts page trees, so a short staleTime replayed a
        // loader on every return. TanStack's documented lever for that is
        // staleTime, not refetchOnMount: a cached row inside the window
        // renders instantly, and invalidateQueries / manual refetch still
        // refresh it.
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Deliberately longer than staleTime. When the two matched, an entry
        // was evicted at the same moment it went stale, so a tab left for more
        // than five minutes came back to an empty cache and refetched
        // everything from a skeleton -- and a query setting
        // `refetchOnMount: false` could not help, because there was no entry
        // left to serve. With a longer window the return paints the cached
        // rows immediately and the refresh happens behind them.
        gcTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        // One retry, not TanStack's default of three. A failing endpoint
        // used to cost four requests per query, which is what turned a
        // single backend hiccup into hundreds of requests on one page load.
        retry: 1,
      },
      dehydrate: {
        // Also dehydrate queries that are still pending, so a Server Component
        // can start a request without awaiting it and let the result stream
        // into the client cache when it lands.
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === 'pending',
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

/**
 * Get the QueryClient for the current environment.
 *
 * On the server this MUST be a new client per call: a module-level singleton is
 * shared by every concurrent request in the same Node process, which would let
 * one user's cached queries be served to another. In the browser there is only
 * ever one user, so the client is created once and reused, which is what keeps
 * cache reads outside React (the zustand stores) hitting the same cache the
 * hooks use.
 */
export function getQueryClient(): QueryClient {
  if (environmentManager.isServer()) {
    return makeQueryClient();
  }

  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
}
